// =========================================================================
// Device detection — kenali platform, kelas device, & kapabilitas dari
// User-Agent + navigator. Dipakai untuk:
//   - lapor player_type asli ke dashboard (telemetry)
//   - default perilaku adaptif (touch lock di TV, autoplay, kursor, dll)
//
// Wrapper APK/Electron boleh inject `window.__NETBIZ_DEVICE__` untuk
// override deteksi (mis. APK Android TV set platform pasti).
// =========================================================================

/** Platform mentah (OS/perangkat). */
export type Platform =
  | "android-tv"
  | "firetv"
  | "tizen" // Samsung TV
  | "webos" // LG TV
  | "chromecast"
  | "android-mobile"
  | "ios"
  | "windows"
  | "macos"
  | "chromeos"
  | "linux"
  | "web";

/** Kelas device — dipakai untuk keputusan UI/perilaku. */
export type DeviceClass = "tv" | "tablet" | "phone" | "desktop" | "kiosk";

/** player_type enum backend (packages/shared PlayerTypeSchema). */
export type PlayerType =
  | "android"
  | "firetv"
  | "web"
  | "windows"
  | "tizen"
  | "webos"
  | "chrome";

export interface DeviceInfo {
  platform: Platform;
  deviceClass: DeviceClass;
  playerType: PlayerType;
  isTV: boolean;
  touch: boolean;
  screen: { w: number; h: number };
  ua: string;
}

interface DeviceOverride {
  platform?: Platform;
  deviceClass?: DeviceClass;
}

function rawDetect(): { platform: Platform; isTV: boolean } {
  const ua = navigator.userAgent || "";
  // Marker eksplisit dari wrapper APK Netbiz (paling andal) —
  if (/NetbizPlayer\/AndroidTV/i.test(ua)) return { platform: "android-tv", isTV: true };
  if (/NetbizPlayer\/Android\b/i.test(ua)) return { platform: "android-mobile", isTV: false };
  // TV / STB dulu (paling spesifik) —
  if (/\bAFT|Fire ?TV\b/i.test(ua)) return { platform: "firetv", isTV: true };
  if (/\bTizen\b/i.test(ua)) return { platform: "tizen", isTV: true };
  if (/\bWeb0S|webOS\b/i.test(ua) || /SmartTV/i.test(ua)) return { platform: "webos", isTV: true };
  if (/\bCrKey\b/i.test(ua)) return { platform: "chromecast", isTV: true };
  // Android TV: UA "TV" / GoogleTV / BRAVIA, atau Android tanpa touch (lean-back)
  const isAndroid = /\bAndroid\b/i.test(ua);
  if (isAndroid && (/\bTV\b|GoogleTV|Android ?TV|BRAVIA|AOSP|ADT-/i.test(ua) || navigator.maxTouchPoints === 0)) {
    return { platform: "android-tv", isTV: true };
  }
  if (isAndroid) return { platform: "android-mobile", isTV: false };
  if (/\biPhone|iPad|iPod\b/i.test(ua)) return { platform: "ios", isTV: false };
  if (/\bCrOS\b/i.test(ua)) return { platform: "chromeos", isTV: false };
  if (/\bWindows\b/i.test(ua)) return { platform: "windows", isTV: false };
  if (/\bMac OS X\b/i.test(ua)) return { platform: "macos", isTV: false };
  if (/\bLinux\b/i.test(ua)) return { platform: "linux", isTV: false };
  return { platform: "web", isTV: false };
}

function classify(platform: Platform, isTV: boolean, touch: boolean, w: number, h: number): DeviceClass {
  if (isTV) return "tv";
  const minSide = Math.min(w, h);
  if (platform === "android-mobile" || platform === "ios") {
    return touch && minSide >= 600 ? "tablet" : "phone";
  }
  // Desktop OS — anggap kiosk kalau fullscreen (lean-back di mini-PC).
  return "desktop";
}

function toPlayerType(platform: Platform): PlayerType {
  switch (platform) {
    case "firetv":
      return "firetv";
    case "tizen":
      return "tizen";
    case "webos":
      return "webos";
    case "android-tv":
    case "android-mobile":
      return "android";
    case "windows":
      return "windows";
    case "chromeos":
    case "chromecast":
      return "chrome";
    default:
      return "web";
  }
}

let cached: DeviceInfo | null = null;

/** Deteksi device (di-cache). Wrapper bisa override via window.__NETBIZ_DEVICE__. */
export function detectDevice(): DeviceInfo {
  if (cached) return cached;
  const override = (window as unknown as { __NETBIZ_DEVICE__?: DeviceOverride }).__NETBIZ_DEVICE__;
  const raw = rawDetect();
  const platform = override?.platform ?? raw.platform;
  const isTV = override?.platform
    ? ["android-tv", "firetv", "tizen", "webos", "chromecast"].includes(platform)
    : raw.isTV;
  const w = window.screen?.width || window.innerWidth || 1920;
  const h = window.screen?.height || window.innerHeight || 1080;
  // TV dianggap non-touch walau UA lapor touch (remote, bukan sentuh).
  const touch = isTV ? false : (navigator.maxTouchPoints ?? 0) > 0;
  const deviceClass = override?.deviceClass ?? classify(platform, isTV, touch, w, h);
  cached = { platform, deviceClass, playerType: toPlayerType(platform), isTV, touch, screen: { w, h }, ua: navigator.userAgent };
  return cached;
}
